import React from 'react';
import { Popover, Tooltip } from 'antd';
import {
  toothColors,
  toothSizes,
  conditionRenderers,
  findingTypes,
  codeByKey,
  resolveColor
} from './odontogramConfig';

export const getSurfaceLabel = (toothNumber, surface) => {
  if (!surface) return null;
  if (surface === 'A' || surface.startsWith('A_')) return 'Apical';
  const quadrant = Math.floor(toothNumber / 10);
  const isUpper = [1, 2, 5, 6].includes(quadrant);
  const isRightQuadrant = [1, 4, 5, 8].includes(quadrant);
  if (surface.startsWith('O')) return 'Oclusal';
  switch (surface) {
    case 'V':
      return isUpper ? 'Vestibular' : 'Lingual';
    case 'L':
      return isUpper ? 'Palatino' : 'Vestibular';
    case 'M':
      return isRightQuadrant ? 'Mesial' : 'Distal';
    case 'D':
      return isRightQuadrant ? 'Distal' : 'Mesial';
    default:
      return surface;
  }
};

export const ToothDiagram = ({
  number,
  findings = [],
  onSurfaceClick,
  onApplyFinding,
  disabled = false,
  innerPadding = 7
}) => {
  const svgW = 40;
  const svgH = 110;

  const lastDigit = number % 10;
  const quadrant = Math.floor(number / 10);

  const isUpper = [1, 2, 5, 6].includes(quadrant);
  const isLower = [3, 4, 7, 8].includes(quadrant);
  const isPermanent = [1, 2, 3, 4].includes(quadrant);

  const isThirdMolar = lastDigit === 8;
  const isStandardMolar = lastDigit === 6 || lastDigit === 7 || (!isPermanent && (lastDigit === 4 || lastDigit === 5));
  const isPremolar = isPermanent && (lastDigit === 4 || lastDigit === 5);
  const isAnterior = lastDigit >= 1 && lastDigit <= 3;

  const crownTop = isUpper ? 60 : 20;
  const crownBottom = isUpper ? 90 : 50;
  const rootBase = isUpper ? 60 : 50;
  const rootPeak = isUpper ? 20 : 90;
  const numberY = isUpper ? 12 : 105;

  const getSurfaceColor = (surface) => {
    const fillFinding = findings.find(finding => finding.surface === surface && (finding.type === codeByKey.caries || finding.type === codeByKey.restoration));
    if (fillFinding) return resolveColor(fillFinding);
    return 'transparent';
  };

  const strokeProps = {
    stroke: toothColors.neutral,
    strokeWidth: "1.2",
    strokeLinejoin: "round",
    strokeLinecap: "round"
  };

  const drawRoots = () => {
    const getApicalColor = (surface) => {
      const finding = findings.find(item => item.surface === surface && (item.type === codeByKey.caries || item.type === codeByKey.restoration));
      return finding ? resolveColor(finding) : null;
    };

    const apical = (closedPath, surfaceId) => {
      const color = getApicalColor(surfaceId);
      return (
        <path
          key={`apical-${surfaceId}`}
          d={closedPath}
          fill={color || 'rgba(255,255,255,0.01)'}
          stroke="none"
          pointerEvents="all"
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          onClick={(e) => { e.stopPropagation(); onSurfaceClick?.(number, surfaceId); }}
        />
      );
    };

    if (isUpper && (isStandardMolar || isThirdMolar)) {
      const peakLeftX = 7; const peakCenterX = 20; const peakRightX = 33;
      const peakY = rootPeak;
      const baseY = rootBase;
      const tripleRootUpperMolar = (
        <g fill="none" {...strokeProps}>
          <path d={`M 0,${baseY} L ${peakLeftX},${peakY} L 14,${baseY}`} />
          <path d={`M 13,${baseY} L ${peakCenterX},${peakY} L 27,${baseY}`} />
          <path d={`M 26,${baseY} L ${peakRightX},${peakY} L 40,${baseY}`} />
          {apical(`M 0,${baseY} L ${peakLeftX},${peakY} L 14,${baseY} Z`, 'A_0')}
          {apical(`M 13,${baseY} L ${peakCenterX},${peakY} L 27,${baseY} Z`, 'A_1')}
          {apical(`M 26,${baseY} L ${peakRightX},${peakY} L 40,${baseY} Z`, 'A_2')}
        </g>
      );
      return tripleRootUpperMolar;
    }

    if (isLower && (isStandardMolar || isThirdMolar)) {
      const doubleRootLowerMolar = (
        <g fill="none" {...strokeProps}>
          <path d={`M 0,${rootBase} L 10,${rootPeak} L 20,${rootBase}`} />
          <path d={`M 20,${rootBase} L 30,${rootPeak} L 40,${rootBase}`} />
          {apical(`M 0,${rootBase} L 10,${rootPeak} L 20,${rootBase} Z`, 'A_0')}
          {apical(`M 20,${rootBase} L 30,${rootPeak} L 40,${rootBase} Z`, 'A_1')}
        </g>
      );
      return doubleRootLowerMolar;
    }

    if (isUpper && lastDigit === 4 && isPermanent) {
      const doubleRootUpperPremolar = (
        <g fill="none" {...strokeProps}>
          <path d={`M 0,${rootBase} L 10,${rootPeak} L 20,${rootBase}`} />
          <path d={`M 20,${rootBase} L 30,${rootPeak} L 40,${rootBase}`} />
          {apical(`M 0,${rootBase} L 10,${rootPeak} L 20,${rootBase} Z`, 'A_0')}
          {apical(`M 20,${rootBase} L 30,${rootPeak} L 40,${rootBase} Z`, 'A_1')}
        </g>
      );
      return doubleRootUpperPremolar;
    }

    const singleRootPath = `M 0,${rootBase} L 20,${rootPeak} L 40,${rootBase}`;
    const singleRootShape = (
      <g fill="none" {...strokeProps}>
        <path d={singleRootPath} />
        {apical(`${singleRootPath} Z`, 'A')}
      </g>
    );
    return singleRootShape;
  };

  const drawCrown = () => {
    const midY = (crownTop + crownBottom) / 2;
    const innerTop = crownTop + innerPadding;
    const innerBottom = crownBottom - innerPadding;
    const innerLeft = innerPadding;
    const innerRight = 40 - innerPadding;

    const renderFill = (pathData, surface) => {
      const color = getSurfaceColor(surface);
      const label = getSurfaceLabel(number, surface);
      const tooltipTitle = label ? `${label} - Pieza ${number}` : `Pieza ${number}`;
      return (
        <Tooltip
          key={`fill-${surface}`}
          title={tooltipTitle}
          mouseEnterDelay={0}
          overlayClassName="odo-surface-tooltip"
        >
          <path
            d={pathData}
            fill={color}
            stroke="none"
            pointerEvents="all"
            onClick={(e) => {
              e.stopPropagation();
              onSurfaceClick?.(number, surface);
            }}
            style={{ cursor: disabled ? 'not-allowed' : undefined }}
          />
        </Tooltip>
      );
    };

    let fills = [];
    if (isAnterior) {
      fills.push(renderFill(`M 0,${crownTop} L 40,${crownTop} L 20,${midY} Z`, 'V'));
      fills.push(renderFill(`M 0,${crownBottom} L 40,${crownBottom} L 20,${midY} Z`, 'L'));
      fills.push(renderFill(`M 0,${crownTop} L 20,${midY} L 0,${crownBottom} Z`, 'D'));
      fills.push(renderFill(`M 40,${crownTop} L 20,${midY} L 40,${crownBottom} Z`, 'M'));
    } else {
      fills.push(renderFill(`M 0,${crownTop} L 40,${crownTop} L ${innerRight},${innerTop} L ${innerLeft},${innerTop} Z`, 'V'));
      fills.push(renderFill(`M 0,${crownBottom} L 40,${crownBottom} L ${innerRight},${innerBottom} L ${innerLeft},${innerBottom} Z`, 'L'));
      fills.push(renderFill(`M 0,${crownTop} L ${innerLeft},${innerTop} L ${innerLeft},${innerBottom} L 0,${crownBottom} Z`, 'D'));
      fills.push(renderFill(`M 40,${crownTop} L ${innerRight},${innerTop} L ${innerRight},${innerBottom} L 40,${crownBottom} Z`, 'M'));

      let columns = 1, rows = 1;
      if (isThirdMolar) { columns = 3; rows = 2; }
      else if (isStandardMolar) { columns = 2; rows = 2; }
      else if (isPremolar) { columns = 1; rows = 2; }

      const cellWidth = (innerRight - innerLeft) / columns;
      const cellHeight = (innerBottom - innerTop) / rows;

      for (let row = 0; row < rows; row++) {
        for (let column = 0; column < columns; column++) {
          const cellX = innerLeft + (column * cellWidth);
          const cellY = innerTop + (row * cellHeight);
          const surfaceId = `O_${row}_${column}`;
          const color = getSurfaceColor(surfaceId);
          fills.push(
            <Tooltip
              key={surfaceId}
              title={`Oclusal - Pieza ${number}`}
              mouseEnterDelay={0}
              overlayClassName="odo-surface-tooltip"
            >
              <rect
                x={cellX} y={cellY} width={cellWidth} height={cellHeight}
                fill={color}
                stroke="none"
                pointerEvents="all"
                onClick={(e) => {
                  e.stopPropagation();
                  onSurfaceClick?.(number, surfaceId);
                }}
                style={{ cursor: disabled ? 'not-allowed' : undefined }}
              />
            </Tooltip>
          );
        }
      }
    }

    const renderLines = () => {
      let columns = 1, rows = 1;
      if (isThirdMolar) { columns = 3; rows = 2; }
      else if (isStandardMolar) { columns = 2; rows = 2; }
      else if (isPremolar) { columns = 1; rows = 2; }

      const cellWidth = (innerRight - innerLeft) / columns;
      const cellHeight = (innerBottom - innerTop) / rows;

      return (
        <g {...strokeProps} pointerEvents="none">
          <rect x="0" y={crownTop} width="40" height={crownBottom - crownTop} fill="none" />

          {isAnterior ? (
            <>
              <line x1="0" y1={crownTop} x2="40" y2={crownBottom} />
              <line x1="40" y1={crownTop} x2="0" y2={crownBottom} />
            </>
          ) : (
            <>
              <rect x={innerLeft} y={innerTop} width={innerRight - innerLeft} height={innerBottom - innerTop} fill="none" />
              <line x1="0" y1={crownTop} x2={innerLeft} y2={innerTop} />
              <line x1="40" y1={crownTop} x2={innerRight} y2={innerTop} />
              <line x1="0" y1={crownBottom} x2={innerLeft} y2={innerBottom} />
              <line x1="40" y1={crownBottom} x2={innerRight} y2={innerBottom} />

              {rows === 2 && <line x1={innerLeft} y1={innerTop + cellHeight} x2={innerRight} y2={innerTop + cellHeight} />}
              {columns >= 2 && <line x1={innerLeft + cellWidth} y1={innerTop} x2={innerLeft + cellWidth} y2={innerBottom} />}
              {columns === 3 && <line x1={innerLeft + (cellWidth * 2)} y1={innerTop} x2={innerLeft + (cellWidth * 2)} y2={innerBottom} />}
            </>
          )}
        </g>
      );
    };

    return (
      <g className="crown-visual">
        {fills}
        {renderLines()}
      </g>
    );
  };

  const hasPegTooth = findings.some(finding => finding.type === codeByKey.pegTooth);

  const drawOverlays = () => {
    return findings.map((finding, idx) => {
      const color = resolveColor(finding);
      const rendererKey = findingTypes[finding.type]?.symbolKey;
      const render = rendererKey ? conditionRenderers[rendererKey] : null;

      if (!render) return null;

      const params = {
        crownTop, rootPeak, crownBottom, rootBase,
        numberY, isUpper, isAnterior,
        innerPadding, isThirdMolar, isStandardMolar, isPremolar,
        color
      };

      return <g key={idx}>{render(finding, params)}</g>;
    });
  };

  const toothSvg = (
    <svg
      width={toothSizes.width * 1.3}
      height={toothSizes.height * 2.5}
      viewBox={`0 0 ${svgW} ${svgH}`}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      onClick={() => onApplyFinding?.(number)}
    >
      <text
        x="20"
        y={numberY}
        textAnchor="middle"
        fontSize={hasPegTooth ? '8' : '11'}
        fontFamily="sans-serif"
        fontWeight="bold"
        fill={toothColors.neutral}
      >
        {number}
      </text>

      {drawRoots()}
      {drawCrown()}
      {drawOverlays()}
    </svg>
  );

  if (!disabled) {
    return (
      <Popover
        content={<div className="tooth-popover-content"> </div>}
        title={`Pieza ${number}`}
        trigger="contextMenu"
      >
        <div data-tooth={number} className="tooth-svg">{toothSvg}</div>
      </Popover>
    );
  }

  return <div data-tooth={number} className="tooth-svg">{toothSvg}</div>;
};